import {
  DistributionChannel,
  OrderResponse,
  OrderStatus,
  ShipmentResponse,
  ShipmentStatus,
  ShippingPartner,
  ShippingType,
} from "@deliverr/replenishment-client";
import { isOutOfStockItemUnremoved, isOutOfStockOrder } from "./details/out-of-stock-items/OutOfStockOrderUtils";
import { CreateTransferLoader } from "transfers/create/CreateTransferLoader.types";
import { addLoader, clearLoader } from "common/components/WithLoader/LoadingActions";
import { RootState } from "RootReducer";
import { StepNameOrStack } from "common/components/StepContainer";

export const allowCancelStatus: OrderStatus[] = [OrderStatus.DRAFT, OrderStatus.SUBMITTED];

export const checkIsCancellable = (replenishmentOrder: OrderResponse) =>
  allowCancelStatus.includes(replenishmentOrder.orderStatus);

export const checkIsPartiallyFulfillable = (replenishmentOrder: OrderResponse, warehouseId: string | undefined) =>
  replenishmentOrder.orderStatus === OrderStatus.DRAFT &&
  isOutOfStockOrder(replenishmentOrder) &&
  (!!replenishmentOrder.orderItems?.length || replenishmentOrder?.invalidOrderItems?.some(isOutOfStockItemUnremoved)) &&
  !!warehouseId;

export const shouldShowParcelInstructions = (shipment: ShipmentResponse, order: OrderResponse) => {
  return (
    shouldShowShipmentInstructions(shipment, order) &&
    shipment.shippingType === ShippingType.PARCEL &&
    ([ShipmentStatus.CREATED, ShipmentStatus.SUBMITTED, ShipmentStatus.PROCESSING] as ShipmentStatus[]).includes(
      shipment.status
    )
  );
};

export const shouldShowFreightInstructions = (shipment: ShipmentResponse, order: OrderResponse) => {
  return (
    shouldShowShipmentInstructions(shipment, order) &&
    (shipment.shippingType === ShippingType.FTL || shipment.shippingType === ShippingType.LTL) &&
    ([ShipmentStatus.CREATED, ShipmentStatus.SUBMITTED] as ShipmentStatus[]).includes(shipment.status)
  );
};

const shouldShowShipmentInstructions = (shipment: ShipmentResponse, order: OrderResponse) => {
  return (
    (order.distributionChannel === DistributionChannel.FBA_MANUAL ||
      order.distributionChannel === DistributionChannel.WHOLESALE) &&
    shipment.shippingPartner === ShippingPartner.SELLER
  );
};

export const shouldShowBookFreight = (shipment: ShipmentResponse, order: OrderResponse) => {
  return (
    (order.distributionChannel === DistributionChannel.FBA_MANUAL ||
      order.distributionChannel === DistributionChannel.WHOLESALE) &&
    shipment.shippingPartner === ShippingPartner.SELLER &&
    (shipment.shippingType === ShippingType.FTL || shipment.shippingType === ShippingType.LTL) &&
    shipment.status === ShipmentStatus.READY_TO_SHIP
  );
};

export const withNextLoader = (
  nextFn: (dispatch, getState: () => RootState) => Promise<StepNameOrStack>,
  dispatch,
  getState: () => RootState
) => {
  return async () => {
    dispatch(addLoader(CreateTransferLoader.NextStepLoader));
    try {
      return await nextFn(dispatch, getState);
    } finally {
      dispatch(clearLoader(CreateTransferLoader.NextStepLoader));
    }
  };
};
